/**
 * GovernanceHubPageV2 — proper v2 redesign (G118).
 *
 * Dashboard pattern: KPI tiles + per-archetype trust mosaic +
 * drill-down cards. NOT an inventory — this is a posture overview.
 *
 * Reuses every backend code path from v1 GovernanceHubPage:
 *   - usePlatform: agents, registrations, workflows, complianceData,
 *     trustData + their loaders
 *
 * v1 stays as fallback via /protection-hub?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthGovernanceEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Download, Shield, ShieldCheck, ShieldAlert,
  Activity, Users, FileCheck, Layers, ArrowRight,
} from 'lucide-react';
import {
  Button, Pill, Card, MetricCard, Slideout, EmptyState,
} from '../components/v2';
import NewAgentletMenu from '../components/v2/NewAgentletMenu';
import { agentCreateRoute } from '../data/agentArchetypes';

import GovernanceHubPage from './GovernanceHubPage';
import { publishedBand } from '../services/trustBands';

function GovernanceHubPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const platform = usePlatform();

  const {
    agents = [], registrations = [], workflows = [],
    loadAgents, loadGelRegistrations, loadWorkflows,
  } = platform;

  /* G223 — PlatformContext.registrations doesn't get populated by every
   * code path; the /gel-agents page fetches directly. Mirror that here so
   * our GEL registrations KPI matches what the operator sees on /gel-agents. */
  const [gelRegistrations, setGelRegistrations] = useState([]);
  /* G223 — POLICIES ACTIVE previously counted agents with an attached
   * policyName field that doesn't exist on the Agent record. Replace with
   * the canonical count: PolicyConfig records in activationStatus=active. */
  const [activePolicyCount, setActivePolicyCount] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadAgents?.(),
        loadGelRegistrations?.(),
        loadWorkflows?.(),
        /* Inline fetches — fall through silently if endpoints fail. */
        (async () => {
          try {
            const res = await api.gelListRegistrations?.();
            const items = Array.isArray(res?.data?.items) ? res.data.items
                        : Array.isArray(res?.items) ? res.items
                        : Array.isArray(res?.data) ? res.data
                        : Array.isArray(res) ? res : [];
            setGelRegistrations(items);
          } catch { /* leave at last good state */ }
        })(),
        (async () => {
          try {
            const res = await api.getPolicyConfigs?.();
            const items = Array.isArray(res?.data?.items) ? res.data.items
                        : Array.isArray(res?.items) ? res.items
                        : Array.isArray(res?.data) ? res.data
                        : Array.isArray(res) ? res : [];
            const active = items.filter(p =>
              (p?.activationStatus || p?.status || '').toLowerCase() === 'active'
            ).length;
            setActivePolicyCount(active);
          } catch { /* leave at last good state */ }
        })(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [loadAgents, loadGelRegistrations, loadWorkflows]);

  useEffect(() => { reload(); }, [reload]);

  /* ── Type selector — Lets / ExteLets / All ────────────────── */
  const [scope, setScope] = useState('all'); // 'lets' | 'extelets' | 'all'

  /* ── Compute KPIs from agent set ─────────────────────────── */
  const scopedAgents = useMemo(() => {
    if (scope === 'lets') return agents.filter(a => !(a.isExternal || a.agentClassification === 'EXTERNAL'));
    if (scope === 'extelets') return agents.filter(a => a.isExternal || a.agentClassification === 'EXTERNAL');
    return agents;
  }, [agents, scope]);

  const kpis = useMemo(() => {
    const total = scopedAgents.length;
    // Canonical trust = complianceScore; Unrated (null) agents are EXCLUDED from
    // the average (averaging a fabricated 0/75 skewed the org posture).
    // See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md.
    const trustScores = scopedAgents.map(a => a.complianceScore).filter(s => typeof s === 'number');
    const trustAvg = trustScores.length > 0 ? Math.round(trustScores.reduce((s, x) => s + x, 0) / trustScores.length) : 0;
    /* G223 — canonical "policies active" comes from the policy-config endpoint,
     * not from a per-agent flag. activePolicyCount is null while loading so we
     * fall back to the legacy per-agent count to avoid flicker on first paint. */
    const policiesActive = activePolicyCount != null
      ? activePolicyCount
      : scopedAgents.filter(a => a.policyName || a.gelPolicyName).length;
    const compliancePct = total > 0 ? Math.round((scopedAgents.filter(a => typeof a.complianceScore === 'number' && a.complianceScore >= 80).length / total) * 100) : 0;
    // Risky = RATED below 75. Unrated (null) is not "risky", it is "no evidence yet".
    const riskyAgents = scopedAgents.filter(a => typeof a.complianceScore === 'number' && a.complianceScore < 75).length;
    const openFindings = scopedAgents.reduce((s, a) => s + (a.openFindings || 0), 0);
    return { total, trustAvg, policiesActive, compliancePct, riskyAgents, openFindings };
  }, [scopedAgents, activePolicyCount]);

  /* ── Trust mosaic — group agents into trust bands ─────────── */
  // Canonical complianceScore; Unrated (null) agents are excluded from all bands
  // (they have no evidence to place). See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md.
  /* Bucketed by the published bands, through the shared service. These were 90/75 with their own
   * labels, so the fleet breakdown here disagreed with the score on each agent's own card. */
  const trustBands = useMemo(() => {
    const of = (...keys) => scopedAgents.filter(
      a => typeof a.complianceScore === 'number' && keys.includes(publishedBand(a.complianceScore)));
    return { strong: of('strong'), adequate: of('adequate'), weak: of('limited', 'weak', 'critical') };
  }, [scopedAgents]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      {/* Header */}
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Protection Command Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Unified protection, trust, policy, and compliance posture across all agents.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} className={refreshing ? 'spin' : ''} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" iconLeft={<Download size={13} />} onClick={() => addToast({ tone: 'info', title: 'Export queued' })}>Export</Button>
          <NewAgentletMenu onPick={(key) => navigate(agentCreateRoute(key))} size="sm" />
        </div>
      </header>

      {/* Type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {[
          { id: 'all',      label: 'All Agents',     icon: Shield,      count: agents.length },
          { id: 'lets',     label: 'Lets (native)',  icon: ShieldCheck, count: agents.filter(a => !(a.isExternal || a.agentClassification === 'EXTERNAL')).length },
          { id: 'extelets', label: 'ExteLets',       icon: ShieldAlert, count: agents.filter(a => a.isExternal || a.agentClassification === 'EXTERNAL').length },
        ].map(opt => {
          const active = scope === opt.id;
          const Icon = opt.icon;
          return (
            <button key={opt.id} onClick={() => setScope(opt.id)}
              className={'p-4 rounded-v2-md border-2 cursor-pointer text-left transition-colors duration-v2 ' + (active
                ? 'border-v2-action bg-v2-action-soft'
                : 'border-v2-border-subtle bg-v2-surface hover:border-v2-action')}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={active ? 'text-v2-action' : 'text-v2-ink-muted'} />
                <span className={'text-[13px] font-bold ' + (active ? 'text-v2-action-strong' : 'text-v2-ink')}>{opt.label}</span>
              </div>
              <div className={'text-[24px] font-extrabold tabular-nums ' + (active ? 'text-v2-action-strong' : 'text-v2-ink')}>{opt.count}</div>
            </button>
          );
        })}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard label="Agents in scope" value={kpis.total} tone="cyan" />
        <MetricCard label="Trust avg" value={kpis.trustAvg ? `${kpis.trustAvg}` : '—'} tone={kpis.trustAvg >= 80 ? 'success' : kpis.trustAvg >= 60 ? 'warning' : 'danger'}
          delta={{ value: '/100', direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Policies active" value={kpis.policiesActive} tone="action" />
        <MetricCard label="Compliance %" value={`${kpis.compliancePct}%`} tone={kpis.compliancePct >= 80 ? 'success' : 'warning'} />
        <MetricCard label="Risky agents" value={kpis.riskyAgents} tone={kpis.riskyAgents > 0 ? 'warning' : 'success'} />
        <MetricCard label="Open findings" value={kpis.openFindings} tone={kpis.openFindings > 0 ? 'warning' : 'success'} />
      </div>

      {/* Drill-down cards */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Trust mosaic */}
        <Card>
          <Card.Header title="Trust mosaic" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/agents')}>All Agentlets</Button>
          } />
          <Card.Body>
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-[12.5px] text-v2-ink"><strong className="text-v2-success">Strong</strong> · ≥ 85</span>
                <span className="text-[12px] text-v2-ink-secondary tabular-nums">{trustBands.strong.length} agents</span>
              </div>
              <div className="h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden">
                <div className="h-full bg-v2-success" style={{ width: `${kpis.total > 0 ? (trustBands.strong.length / kpis.total) * 100 : 0}%` }} />
              </div>
              <div className="flex items-baseline justify-between mt-3">
                <span className="text-[12.5px] text-v2-ink"><strong className="text-v2-warning">Adequate</strong> · 70-84</span>
                <span className="text-[12px] text-v2-ink-secondary tabular-nums">{trustBands.adequate.length} agents</span>
              </div>
              <div className="h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden">
                <div className="h-full bg-v2-warning" style={{ width: `${kpis.total > 0 ? (trustBands.adequate.length / kpis.total) * 100 : 0}%` }} />
              </div>
              <div className="flex items-baseline justify-between mt-3">
                <span className="text-[12.5px] text-v2-ink"><strong className="text-v2-danger">At risk</strong> · &lt; 70</span>
                <span className="text-[12px] text-v2-ink-secondary tabular-nums">{trustBands.weak.length} agents</span>
              </div>
              <div className="h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden">
                <div className="h-full bg-v2-danger" style={{ width: `${kpis.total > 0 ? (trustBands.weak.length / kpis.total) * 100 : 0}%` }} />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Compliance */}
        <Card>
          <Card.Header title="Compliance posture" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/compliance')}>Compliance Center</Button>
          } />
          <Card.Body>
            <div className="text-[36px] font-extrabold tabular-nums leading-none mb-2"
              style={{ color: kpis.compliancePct >= 80 ? 'var(--v2-success, #047857)' : 'var(--v2-warning, #92400e)' }}>
              {kpis.compliancePct}%
            </div>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Of {kpis.total} agent{kpis.total === 1 ? '' : 's'} in scope, {scopedAgents.filter(a => (a.complianceScore || 0) >= 80).length} are passing the 80% compliance bar.
            </p>
            <div className="text-[12px] text-v2-ink-muted mt-3">{kpis.openFindings} open finding{kpis.openFindings === 1 ? '' : 's'} across all frameworks.</div>
          </Card.Body>
        </Card>

        {/* Recent agents requiring attention */}
        <Card>
          <Card.Header title="Needs attention" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/agents')}>All Agentlets</Button>
          } />
          <Card.Body>
            {trustBands.weak.length === 0 ? (
              <p className="text-[12.5px] text-v2-ink-secondary">All agents in scope are above the 75 trust threshold. ✓</p>
            ) : (
              <ul className="space-y-2 text-[12.5px]">
                {trustBands.weak.slice(0, 5).map(a => (
                  <li key={a.id}>
                    <button onClick={() => navigate(`/agents/${a.id}`)}
                      className="w-full text-left px-2 py-1.5 rounded-v2-sm hover:bg-v2-muted flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-v2-danger flex-shrink-0" />
                      <span className="font-semibold text-v2-ink truncate">{a.name || a.id}</span>
                      <span className="ml-auto font-mono-v2 text-[11px] text-v2-ink-muted">{typeof a.complianceScore === 'number' ? a.complianceScore : 'Unrated'}</span>
                    </button>
                  </li>
                ))}
                {trustBands.weak.length > 5 && (
                  <li className="text-[11.5px] text-v2-ink-muted pt-1">+ {trustBands.weak.length - 5} more</li>
                )}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* Discovery feed */}
        <Card>
          <Card.Header title="Recent discoveries" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/shadow-discovery')}>Discovery</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary">
              Shadow agents and discovered workflows surface here for triage.
              <span className="block mt-2 text-v2-ink">Open Discovery to inspect new entries.</span>
            </p>
          </Card.Body>
        </Card>

        {/* Workflows */}
        <Card>
          <Card.Header title="Active workflows" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/canvas')}>Canvas</Button>
          } />
          <Card.Body>
            <div className="text-[28px] font-extrabold tabular-nums text-v2-ink leading-none mb-1">{workflows.length}</div>
            <p className="text-[12.5px] text-v2-ink-secondary">workflows under protection</p>
          </Card.Body>
        </Card>

        {/* GEL registrations */}
        <Card>
          <Card.Header title="GEL registrations" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />} onClick={() => navigate('/gel-agents')}>Manage</Button>
          } />
          <Card.Body>
            {/* G223 — prefer the inline-fetched list which mirrors what
              * /gel-agents shows; fall back to PlatformContext for resilience. */}
            <div className="text-[28px] font-extrabold tabular-nums text-v2-ink leading-none mb-1">
              {(gelRegistrations.length || registrations.length || 0).toLocaleString()}
            </div>
            <p className="text-[12.5px] text-v2-ink-secondary">
              external agent registrations
              {gelRegistrations.length > 0 && (() => {
                const verified = gelRegistrations.filter(r => {
                  const s = (r?.registrationStatus || r?.status || '').toUpperCase();
                  return s === 'VERIFIED' || s === 'ACTIVE';
                }).length;
                return verified > 0 ? <span> · {verified} verified</span> : null;
              })()}
            </p>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

export default function ResolvedGovernanceHubPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthGovernanceEnabled()) return <GovernanceHubPage {...props} />;
  return <GovernanceHubPageV2Inner {...props} />;
}

export { GovernanceHubPageV2Inner };
